import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { chequesApi } from '../services/api';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Plus, Search, MoreVertical, CheckCircle2, XCircle, Clock, Eye, Download, Trash2, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = ['EUR', 'USD', 'CHF', 'GBP'];

const statusConfig = {
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  executed: { label: 'Encaisse', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejete', color: 'bg-red-100 text-red-700 border-red-200' },
};

export default function ChequesPage() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin';
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [creating, setCreating] = useState(false);
  const [banks, setBanks] = useState([]);
  const [view, setView] = useState('mine');
  const [form, setForm] = useState({
    beneficiary_name: '', client_email: '', amount: '', currency: 'EUR',
    amount_in_words: '', sender_bank: '', agency_name: '', agency_address: '',
    guichet_number: '', account_number: '', cheque_number: '', location: '',
    payment_reason: '', client_language: 'fr',
  });

  const fetchCheques = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (isAdmin && view) params.view = view;
      if (search) params.search = search;
      const res = await chequesApi.getAll(params);
      setCheques(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [isAdmin, view, search]);

  useEffect(() => { fetchCheques(); }, [fetchCheques]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sender-banks`).then(r => setBanks(r.data || [])).catch(() => {});
  }, []);

  const handleCreate = async () => {
    if (!form.beneficiary_name || !form.client_email || !form.amount || !form.sender_bank || !form.amount_in_words) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setCreating(true);
    try {
      await chequesApi.create({ ...form, amount: parseFloat(form.amount) });
      toast.success('Cheque cree avec succes');
      setShowCreate(false);
      setForm({ beneficiary_name: '', client_email: '', amount: '', currency: 'EUR', amount_in_words: '', sender_bank: '', agency_name: '', agency_address: '', guichet_number: '', account_number: '', cheque_number: '', location: '', payment_reason: '', client_language: 'fr' });
      fetchCheques();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setCreating(false);
    }
  };

  const handleAction = async (action, cheque) => {
    try {
      if (action === 'execute') { await chequesApi.execute(cheque.id); toast.success('Cheque encaisse'); }
      else if (action === 'pending') { await chequesApi.setPending(cheque.id); toast.success('Cheque mis en attente'); }
      else if (action === 'reject') { await chequesApi.reject(cheque.id, {}); toast.success('Cheque rejete'); }
      else if (action === 'delete') { await chequesApi.delete(cheque.id); toast.success('Cheque supprime'); }
      else if (action === 'pdf') {
        const res = await chequesApi.downloadPdf(cheque.id);
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url; a.download = `WorldPay_Cheque_${cheque.id.slice(0,8).toUpperCase()}.pdf`; a.click();
        window.URL.revokeObjectURL(url); return;
      }
      fetchCheques();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const formatAmount = (a, c) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: c || 'EUR' }).format(a);
  const formatDate = (d) => { if (!d) return ''; try { return new Date(d).toLocaleDateString('fr-FR', { day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'}); } catch { return d; }};

  return (
    <Layout titleOverride="Cheques" subtitleOverride="Gestion des cheques bancaires">
      <div className="space-y-4" data-testid="cheques-page">
        {/* Tabs */}
        {isAdmin && (
          <div className="flex gap-2">
            {['mine','users','all'].map(v => (
              <button key={v} onClick={() => setView(v === 'all' ? '' : v)} data-testid={`cheques-tab-${v}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${(view === v || (v === 'all' && !view)) ? 'bg-[#0066FF] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {v === 'mine' ? 'Mes cheques' : v === 'users' ? 'Cheques utilisateurs' : 'Tous'}
              </button>
            ))}
          </div>
        )}

        {/* Search + Create */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="cheques-search" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchCheques} data-testid="cheques-refresh"><RefreshCw className="w-4 h-4" /></Button>
            <Button onClick={() => setShowCreate(true)} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="cheques-create-btn">
              <Plus className="w-4 h-4 mr-2" />Nouveau Cheque
            </Button>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
            ) : cheques.length === 0 ? (
              <div className="text-center py-12 text-gray-500">Aucun cheque trouve</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="cheques-table">
                  <thead><tr className="border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="text-left p-3 font-medium">Beneficiaire</th>
                    <th className="text-left p-3 font-medium">Banque</th>
                    <th className="text-left p-3 font-medium">Email</th>
                    <th className="text-right p-3 font-medium">Montant</th>
                    <th className="text-center p-3 font-medium">Statut</th>
                    <th className="text-left p-3 font-medium">Date</th>
                    <th className="text-right p-3 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {cheques.map(ch => (
                      <tr key={ch.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`cheque-row-${ch.id}`}>
                        <td className="p-3"><div className="font-medium text-gray-900">{ch.beneficiary_name}</div><div className="text-xs text-gray-500">{ch.cheque_number || '-'}</div></td>
                        <td className="p-3 text-gray-600">{ch.sender_bank}</td>
                        <td className="p-3 text-gray-600">{ch.client_email}</td>
                        <td className="p-3 text-right font-semibold">{formatAmount(ch.amount, ch.currency)}</td>
                        <td className="p-3 text-center"><Badge className={`${statusConfig[ch.status]?.color || ''} text-xs`}>{statusConfig[ch.status]?.label || ch.status}</Badge></td>
                        <td className="p-3 text-gray-500 text-xs">{formatDate(ch.created_at)}</td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`cheque-actions-${ch.id}`}><MoreVertical className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => { setSelectedCheque(ch); setShowDetail(true); }}><Eye className="w-4 h-4 mr-2" />Voir details</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleAction('pdf', ch)}><Download className="w-4 h-4 mr-2" />Telecharger PDF</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {ch.status !== 'executed' && <DropdownMenuItem onClick={() => handleAction('execute', ch)} className="text-emerald-600"><CheckCircle2 className="w-4 h-4 mr-2" />Encaisser</DropdownMenuItem>}
                              {ch.status !== 'pending' && <DropdownMenuItem onClick={() => handleAction('pending', ch)} className="text-amber-600"><Clock className="w-4 h-4 mr-2" />Mettre en attente</DropdownMenuItem>}
                              {ch.status !== 'rejected' && <DropdownMenuItem onClick={() => handleAction('reject', ch)} className="text-red-600"><XCircle className="w-4 h-4 mr-2" />Rejeter</DropdownMenuItem>}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleAction('delete', ch)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Supprimer</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nouveau Cheque</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Beneficiaire *</Label><Input value={form.beneficiary_name} onChange={e => setForm({...form, beneficiary_name: e.target.value})} data-testid="cheque-form-beneficiary" /></div>
              <div><Label>Email client *</Label><Input type="email" value={form.client_email} onChange={e => setForm({...form, client_email: e.target.value})} data-testid="cheque-form-email" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Montant *</Label><Input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="cheque-form-amount" /></div>
                <div><Label>Devise</Label>
                  <Select value={form.currency} onValueChange={v => setForm({...form, currency: v})}>
                    <SelectTrigger data-testid="cheque-form-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Montant en lettres *</Label><Input value={form.amount_in_words} onChange={e => setForm({...form, amount_in_words: e.target.value})} placeholder="cinquante-trois mille euros" data-testid="cheque-form-words" /></div>
              <div><Label>Banque emettrice *</Label>
                <Select value={form.sender_bank} onValueChange={v => setForm({...form, sender_bank: v})}>
                  <SelectTrigger data-testid="cheque-form-bank"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                  <SelectContent>{banks.map(b => { const n = typeof b === 'string' ? b : b.name; return <SelectItem key={n} value={n}>{n}</SelectItem>; })}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nom agence</Label><Input value={form.agency_name} onChange={e => setForm({...form, agency_name: e.target.value})} data-testid="cheque-form-agency" /></div>
                <div><Label>N. guichet</Label><Input value={form.guichet_number} onChange={e => setForm({...form, guichet_number: e.target.value})} data-testid="cheque-form-guichet" /></div>
              </div>
              <div><Label>Adresse agence</Label><Input value={form.agency_address} onChange={e => setForm({...form, agency_address: e.target.value})} data-testid="cheque-form-address" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>N. compte</Label><Input value={form.account_number} onChange={e => setForm({...form, account_number: e.target.value})} data-testid="cheque-form-account" /></div>
                <div><Label>N. cheque</Label><Input value={form.cheque_number} onChange={e => setForm({...form, cheque_number: e.target.value})} data-testid="cheque-form-cheque-num" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Lieu</Label><Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Paris" data-testid="cheque-form-location" /></div>
                <div><Label>Motif</Label><Input value={form.payment_reason} onChange={e => setForm({...form, payment_reason: e.target.value})} data-testid="cheque-form-reason" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-[#0066FF] hover:bg-[#0052CC]" data-testid="cheque-form-submit">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Creer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Details du Cheque</DialogTitle></DialogHeader>
            {selectedCheque && (
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">{formatAmount(selectedCheque.amount, selectedCheque.currency)}</p>
                  <Badge className={`mt-2 ${statusConfig[selectedCheque.status]?.color}`}>{statusConfig[selectedCheque.status]?.label}</Badge>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Beneficiaire</span><span className="font-medium">{selectedCheque.beneficiary_name}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Montant en lettres</span><span className="font-medium text-xs">{selectedCheque.amount_in_words}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Banque</span><span className="font-medium">{selectedCheque.sender_bank}</span></div>
                  {selectedCheque.agency_name && <div className="flex justify-between"><span className="text-gray-500">Agence</span><span className="font-medium">{selectedCheque.agency_name}</span></div>}
                  {selectedCheque.cheque_number && <div className="flex justify-between"><span className="text-gray-500">N. cheque</span><span className="font-medium">{selectedCheque.cheque_number}</span></div>}
                  {selectedCheque.account_number && <div className="flex justify-between"><span className="text-gray-500">N. compte</span><span className="font-medium">{selectedCheque.account_number}</span></div>}
                  {selectedCheque.location && <div className="flex justify-between"><span className="text-gray-500">Lieu</span><span className="font-medium">{selectedCheque.location}</span></div>}
                  <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-medium">{formatDate(selectedCheque.created_at)}</span></div>
                </div>
                <Button variant="outline" className="w-full" onClick={() => handleAction('pdf', selectedCheque)} data-testid="cheque-detail-pdf">
                  <Download className="w-4 h-4 mr-2" />Telecharger le cheque PDF
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
